// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductDetailsPageContext } from "@insite/content-library/Pages/ProductDetailsPage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import createReferenceNumber from "@mcmc/App/Handlers/CustomerReference/CreateCustomerReferenceNumber";
import deleteReferenceNumber from "@mcmc/App/Handlers/CustomerReference/DeleteCustomerReferenceNumber";
import updateReferenceNumber from "@mcmc/App/Handlers/CustomerReference/UpdateCustomerReferenceNumber";
import updateProduct from "@mcmc/App/Handlers/Products/UpdateProductData";
import { labelTextStyle, valueTextStyle } from "@mcmc/App/WidgetExtensions/ProductDetails/ProductDetailsExtensions";
import EllipsisSpinner from "@mcmc/XDL/Components/Common/EllipsisSpinner";
import Edit from "@mcmc/XDL/Icons/Edit";
import { mergeWithCss, printStyles } from "@mcmc/XDL/Utilities";
import cloneDeep from "lodash/cloneDeep";
import * as React from "react";
import { createRef, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface RefNumberInputState {
    id: string;
    refNumber: string;
    newRefNumber: string;
    isNew?: boolean;
    isLoading?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    session: state.context.session,
});

const mapDispatchToProps = {
    createReferenceNumber,
    updateReferenceNumber,
    deleteReferenceNumber,
    updateProduct,
};

type Props = WidgetProps & HasProduct & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductDetailsCustomerReferenceNumberStyles {
    wrapper?: InjectableCss;
    labelText?: TypographyProps;
    valueText?: TypographyProps;
    editWrapper?: InjectableCss;
    updateButton?: ButtonPresentationProps;
    sectionTitle?: TypographyPresentationProps;
    editIcon?: IconPresentationProps;
    refTextField?: TextFieldPresentationProps;
    printOnlyValueText?: TypographyPresentationProps;
}

export const customerReferenceNumberStyles: ProductDetailsCustomerReferenceNumberStyles = {
    wrapper: {
        css: printStyles.hideOnPrint,
    },
    labelText: labelTextStyle,
    valueText: mergeWithCss(valueTextStyle, {
        className: "",
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: 100%;
            min-height: 40px;
        `,
    }),
    editWrapper: {
        css: css`
            list-style: none;
            padding: 0 15px 10px 15px;
            margin: 0;

            li {
                display: flex;
                align-items: center;
                margin-bottom: 5px;
            }
        `,
    },
    updateButton: {
        sizeVariant: "small",
        typographyProps: {
            size: 14,
        },
        css: css`
            min-width: 90px;
            margin-left: 5px;
            padding: 0 5px;
        `,
    },
    refTextField: {
        sizeVariant: "small",
        cssOverrides: {
            inputSelect: [
                css`
                    ${getProp("theme.formField.defaultProps.cssOverrides.inputSelect")}
                    height: 30px;
                `,
            ],
        },
    },
    editIcon: {
        src: Edit,
        size: 14,
        color: "secondary",
        css: css`
            position: relative;
            top: 2px;
            margin-left: 5px;
        `,
    },
    printOnlyValueText: mergeWithCss(valueTextStyle, { css: printStyles.showOnPrint }),
};

customerReferenceNumberStyles.sectionTitle = {
    ...customerReferenceNumberStyles.valueText,
    weight: "bold",
};

const createNewRef = (): RefNumberInputState => ({ id: "new", refNumber: "", newRefNumber: "", isNew: true });

const StyledUl = getStyledWrapper("ul");

const styles = customerReferenceNumberStyles;

const ProductDetailsCustomerReferenceNumber: React.FC<Props> = ({
    product,
    session,
    createReferenceNumber,
    updateReferenceNumber,
    deleteReferenceNumber,
    updateProduct,
}) => {
    const [isEditMode, setIsEditMode] = useState(false);
    const [refNumber, setRefNumber] = useState("");
    const [isOpenRefForm, setIsOpenRefForm] = useState(true);
    const [refInputs, setRefInputs] = useState<RefNumberInputState[]>([]);
    const toasterContext = useContext(ToasterContext);
    const wrapperRef = createRef<HTMLDivElement>();
    const isAuthenticated = session.isAuthenticated && !session.isGuest;

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (!isOpenRefForm) {
                const isTargetInsideRef = wrapperRef.current && wrapperRef.current.contains(event.target as Node);
                if (!isTargetInsideRef) {
                    setIsEditMode(false);
                    setIsOpenRefForm(true);
                }
            } else{
                setIsOpenRefForm(false);
            }
        };

        document.body.addEventListener("click", handleClickOutside);
        return () => {
            document.body.removeEventListener("click", handleClickOutside);
        };
    }, [wrapperRef]);

    useEffect(() => {
        let refNumberStates: RefNumberInputState[] = [];
        const refNumber = product.properties.referenceNumbers;
        if (refNumber) {
            const refNumbers = refNumber.split("|");
            setRefNumber(refNumber.split("|").join(" | "));
            refNumberStates = refNumbers.map((refNumber, index) => ({
                id: index + refNumber,
                refNumber,
                newRefNumber: refNumber,
            }));
        } else {
            setRefNumber("");
        }
        setRefInputs([...refNumberStates, createNewRef()]);
    }, [product]);

    if (!isAuthenticated) {
        return null;
    }

    const handleRefChange = (input: RefNumberInputState, newValue: string) => {
        input.newRefNumber = newValue;
        setRefInputs([...refInputs]);
    };

    const onErrorResult = (e: string, input: RefNumberInputState) => {
        input.isLoading = false;
        setRefInputs([...refInputs]);
        toasterContext.addToast({ body: e, messageType: "danger" });
    };

    const onSuccessResult = (input: RefNumberInputState, refInputs: RefNumberInputState[]) => {
        input.isLoading = false;
        setRefInputs([...refInputs]);
        const refNumber = refInputs
            .filter(input => !input.isNew)
            .map(input => input.refNumber)
            .join("|");
        const tmpProduct = cloneDeep(product);
        tmpProduct.properties.referenceNumbers = refNumber;
        updateProduct({ newProductModel: tmpProduct });
    };

    const addOrUpdateRefNumber = (input: RefNumberInputState) => {
        input.isLoading = true;
        setRefInputs([...refInputs]);
        if (input.isNew) {
            createReferenceNumber({
                referenceNumber: input.newRefNumber,
                productId: product.id,
                customerId: session.shipToId || "",
                onSuccess: () => {
                    input.id = refInputs.length + input.newRefNumber;
                    input.isNew = false;
                    input.refNumber = input.newRefNumber;
                    refInputs.push(createNewRef());
                    onSuccessResult(input, refInputs);
                },
                onError: e => onErrorResult(e, input),
            });
        } else if (input.newRefNumber) {
            updateReferenceNumber({
                oldReferenceNumber: input.refNumber,
                newReferenceNumber: input.newRefNumber,
                productId: product.id,
                onSuccess: () => {
                    input.refNumber = input.newRefNumber;
                    onSuccessResult(input, refInputs);
                },
                onError: e => onErrorResult(e, input),
            });
        } else {
            deleteReferenceNumber({
                referenceNumber: input.refNumber,
                productId: product.id,
                onSuccess: () => {
                    const index = refInputs.findIndex(tmpInput => input.refNumber === tmpInput.refNumber);
                    if (index > -1) {
                        const tmpRefInputs = [...refInputs];
                        tmpRefInputs.splice(index, 1);
                        onSuccessResult(input, tmpRefInputs);
                        return;
                    }
                    onSuccessResult(input, refInputs);
                },
                onError: e => onErrorResult(e, input),
            });
        }
    };

    return (
        <>
            <StyledWrapper ref={wrapperRef} className="mcmc-part-number" {...styles.wrapper}>
                {!isEditMode && (
                    <Clickable onClick={() => setIsEditMode(true)} {...styles.valueText}>
                        {refNumber && (
                            <Typography>
                                <Typography {...styles.labelText}>{translate("REF")}:</Typography>
                                {refNumber}
                            </Typography>
                        )}
                        {!refNumber && (
                            <Typography {...styles.labelText}>{translate("Add New Reference #")}</Typography>
                        )}
                        <Icon {...styles.editIcon}></Icon>
                    </Clickable>
                )}
                {isEditMode && (
                    <>
                        <Typography {...styles.valueText}>
                            <Typography {...styles.labelText}>{translate("Edit REFs")}:</Typography>
                        </Typography>
                        <StyledUl {...styles.editWrapper}>
                            {refInputs.map(input => (
                                <li key={input.id}>
                                    <TextField
                                        {...styles.refTextField}
                                        value={input.newRefNumber}
                                        onChange={e => handleRefChange(input, e.currentTarget.value)}
                                        data-test-selector={`ref-input-${input.id}`}
                                    />
                                    <Button
                                        {...styles.updateButton}
                                        onClick={() => addOrUpdateRefNumber(input)}
                                        disabled={input.isLoading || input.newRefNumber === input.refNumber}
                                    >
                                        <EllipsisSpinner
                                            showSpinner={input.isLoading}
                                            label={input.isNew ? translate("Add") : translate("Update")}
                                        />
                                    </Button>
                                </li>
                            ))}
                        </StyledUl>
                    </>
                )}
            </StyledWrapper>
            {refNumber && (
                <Typography {...styles.printOnlyValueText} as="p">
                    <Typography {...styles.labelText}>{translate("REF")}:</Typography>
                    {refNumber}
                </Typography>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withProduct(ProductDetailsCustomerReferenceNumber)),
    definition: {
        displayName: "Customer Reference Number",
        group: "Product Details",
        allowedContexts: [ProductDetailsPageContext],
    },
};

export default widgetModule;
